<script>
import MobileLocaleDock from "@/components/common/MobileLocaleDock.vue";
import WorkflowChatEntry from "@/components/workflow-chat/WorkflowChatEntry.vue";
import ChatWorkbenchHost from "@/components/chat-workbench/ChatWorkbenchHost.vue";
import MobileChatSheet from "@/components/chat-workbench/MobileChatSheet.vue";
import { ChatRealtimeClient } from "@/services/chat-realtime";
import { RealtimeRecovery } from "@/services/realtime-recovery";
import { emitRecoveredRealtime } from "@/services/realtime-hub";
import { getRealtimeResources, getRealtimeEvents } from "@/api/realtimeRecovery";
import { resolveAgentServiceCurrentUserId } from "@/services/agent-service-state";

/** PC保留常驻浮窗；手机消息页和业务页半窗独立承载，共用消息内容。 */
export default {
  components: { WorkflowChatEntry, MobileLocaleDock, ChatWorkbenchHost, MobileChatSheet },
  data() {
    return { presenceClient: null, recoveryClient: null };
  },
  computed: {
    generalChatRequestVersion() { return this.$store.state.agentServiceUi?.generalRequestVersion || 0; },
    isPcUi() {
      const path = this.$route.path;
      return /^\/pc(?:\/|$)/u.test(path) || (path === "/" && this.$device.isPc);
    },
    showMobileLocaleDock() {
      const path = this.$route.path;
      return Boolean(this.$store.state.locale?.enabled) && !/^\/pc(?:\/|$)/u.test(path)
        && (path !== "/" || !this.$device.isPc);
    },
    generalChatUserId() {
      return resolveAgentServiceCurrentUserId(this.$store.state.user || {});
    },
    generalChatOpen() {
      return this.$store.state.agentServiceUi.generalOpen;
    },
  },
  watch: {
    generalChatRequestVersion() {
      if (!this.isPcUi && this.generalChatOpen) this.$store.commit('chatWorkbench/open', { kind: 'GENERAL' });
    },
    isPcUi: {
      immediate: true,
      handler(value) {
        // 根标记也覆盖append-to-body的PC弹窗；切回手机页面自动撤销。
        if (typeof document !== "undefined") document.documentElement.classList.toggle("xsou-pc-ui", value);
      },
    },
    "$store.state.user.userInfo.token"() {
      if (!this.isPcUi) this.$store.commit("chatWorkbench/reset");
      this.$store.commit("generalAgentService/clearIdentity");
      this.$store.commit("agentService/clearIdentity");
      this.$store.commit("agentServiceUi/resetGeneral");
      this.$store.commit("supplyDemandAgentService/clearIdentity");
      this.syncAgentServicePresence();
    },
    generalChatUserId(value, previousValue) {
      if (value === previousValue) return;
      this.syncAgentServicePresence();
      if (!previousValue) return;
      if (!this.isPcUi) this.$store.commit("chatWorkbench/reset");
      this.$store.commit("generalAgentService/clearIdentity");
      this.$store.commit("agentService/clearIdentity");
      this.$store.commit("agentServiceUi/resetGeneral");
      this.$store.commit("supplyDemandAgentService/clearIdentity");
    },
  },
  mounted() {
    this.syncAgentServicePresence();
    document.addEventListener("visibilitychange", this.recoverRealtimeInbox);
  },
  beforeDestroy() {
    document.documentElement.classList.remove("xsou-pc-ui");
    this.closeAgentServicePresence();
    document.removeEventListener("visibilitychange", this.recoverRealtimeInbox);
  },
  methods: {
    /** 登录期间维持共享连接；未打开聊天窗口也接收本人收件箱更新。 */
    syncAgentServicePresence() {
      this.closeAgentServicePresence();
      const token = String(this.$store.state.user?.userInfo?.token || "");
      if (!token) return;
      this.recoveryClient = new RealtimeRecovery({ getResources: getRealtimeResources, getEvents: getRealtimeEvents,
        onEvent: event => emitRecoveredRealtime(token, event), onMore: () => this.presenceClient?.refresh(true),
      });
      this.presenceClient = new ChatRealtimeClient({
        token,
        subscriptions: [{ channel: "customer-service", resourceId: "self" }, { channel: "legacy-chat", resourceId: "self" }, { channel: "workflow-chat", resourceId: "self" }],
        onRefresh: async full => {
          if (document.hidden || !this.generalChatUserId) return;
          await Promise.all([this.$store.dispatch("user/refreshUnreadCount"), full ? this.recoveryClient?.recover() : undefined]);
        },
        onError: () => {},
      });
      this.presenceClient.connect();
    },
    recoverRealtimeInbox() {
      if (!document.hidden) this.presenceClient?.refresh();
    },
    closeAgentServicePresence() {
      this.recoveryClient?.close(); this.recoveryClient = null;
      this.presenceClient?.close();
      this.presenceClient = null;
    },
  },
};
</script>

<template>
  <div id="app" :class="{ 'pc-ui-fixed-text': isPcUi }">
    <router-view />
    <ChatWorkbenchHost v-if="isPcUi" />
    <MobileChatSheet v-else />
    <WorkflowChatEntry />
    <MobileLocaleDock v-if="showMobileLocaleDock" />
    <!-- 测试测试 -->
  </div>
</template>
<style lang="less">
:root {
  --mobile-locale-safe-top: env(safe-area-inset-top, 0px);
  --mobile-locale-safe-bottom: env(safe-area-inset-bottom, 0px);
  --mobile-locale-safe-left: env(safe-area-inset-left, 0px);
  --mobile-locale-safe-right: env(safe-area-inset-right, 0px);
}
</style>
